import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HandCoins, AlertTriangle, BadgeAlert, Banknote, UserSquare2, ClipboardList, RefreshCcw, ArrowDownAZ, TrendingUp, Wallet, Trophy, Clock, CalendarClock, CheckCircle2, XCircle, Scissors } from 'lucide-react';
import { OwnerLayout } from '../../../components/OwnerLayout';
import api from '../../../api';
import { encodeLan } from '../../../api';

function fmtINR(n) {
  const num = Number(n || 0);
  if (num >= 10000000) return `\u20B9${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `\u20B9${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `\u20B9${(num / 1000).toFixed(1)} K`;
  return `\u20B9${num.toFixed(0)}`;
}

const STATUS_LABELS = {
  new_overdue: 'New Overdue',
  contact_attempted: 'Contact Attempted',
  connected: 'Connected',
  ptp_set: 'PTP Set',
  partially_paid: 'Partially Paid',
  regularized: 'Regularized',
  settled_closed: 'Settled / Closed',
  dispute_hold: 'Dispute Hold',
  escalated_supervisor: 'Escalated',
  legal_initiated: 'Legal Initiated',
  written_off: 'Written Off',
};

export default function RecoveryDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [allCases, setAllCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('bouncedEmis');
  const [selectedCollector, setSelectedCollector] = useState(null);
  const [collectorPayments, setCollectorPayments] = useState([]);
  const [collectorLoading, setCollectorLoading] = useState(false);
  const [paymentsDrill, setPaymentsDrill] = useState(null); // { title, from, to }
  const [paymentsDrillData, setPaymentsDrillData] = useState(null);
  const [paymentsDrillLoading, setPaymentsDrillLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = branchId ? { branchId } : {};
      const [sumRes, brRes, casesRes] = await Promise.all([
        api.get('/recovery/dashboard/summary', { params }),
        api.get('/branches'),
        api.get('/recovery/cases', { params }),
      ]);
      setSummary(sumRes.data);
      setBranches(brRes.data || []);
      setAllCases(casesRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const openCollector = async (collector) => {
    setSelectedCollector(collector);
    setCollectorPayments([]);
    setCollectorLoading(true);
    try {
      const params = branchId ? { branchId } : {};
      const collectorId = collector.userId || collector.id;
      const res = await api.get(`/recovery/payments/by-collector/${collectorId}`, { params });
      setCollectorPayments(res.data || []);
    } catch (err) {
      console.error(err);
      setCollectorPayments([]);
    } finally {
      setCollectorLoading(false);
    }
  };

  const closeCollector = () => {
    setSelectedCollector(null);
    setCollectorPayments([]);
    setCollectorLoading(false);
  };

  const ymd = (d) => {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const openPayments = async (title, fromYmd, toYmd) => {
    setPaymentsDrill({ title, from: fromYmd, to: toYmd });
    setPaymentsDrillData(null);
    setPaymentsDrillLoading(true);
    try {
      const params = { limit: 500 };
      if (fromYmd) params.from = fromYmd;
      if (toYmd) params.to = toYmd;
      if (branchId) params.branchId = branchId;
      const res = await api.get('/recovery/payments', { params });
      setPaymentsDrillData(res.data || { items: [], summary: {} });
    } catch (err) {
      console.error(err);
      setPaymentsDrillData({ items: [], summary: {} });
    } finally {
      setPaymentsDrillLoading(false);
    }
  };

  const closePayments = () => {
    setPaymentsDrill(null);
    setPaymentsDrillData(null);
    setPaymentsDrillLoading(false);
  };

  const openTodayPayments = () => {
    const today = ymd(new Date());
    openPayments('Collected Today', today, today);
  };
  const openYesterdayPayments = () => {
    const y = new Date(); y.setUTCDate(y.getUTCDate() - 1);
    const ys = ymd(y);
    openPayments('Collected Yesterday', ys, ys);
  };
  const openWeekPayments = () => {
    const now = new Date();
    const day = now.getUTCDay() || 7; // Mon=1..Sun=7
    const monday = new Date(now); monday.setUTCDate(now.getUTCDate() - (day - 1));
    openPayments('Collected This Week', ymd(monday), ymd(now));
  };
  const openMonthPayments = () => {
    const now = new Date();
    const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    openPayments('Collected This Month', ymd(first), ymd(now));
  };
  const openAllPayments = () => openPayments('All Collections', null, null);

  const sortedCases = useMemo(() => {
    const arr = [...allCases];
    const getNum = (c, k) => Number(c.loan?.[k] ?? c[k] ?? 0);
    if (sortBy === 'bouncedEmis') arr.sort((a, b) => getNum(b, 'bouncedEmis') - getNum(a, 'bouncedEmis') || getNum(b, 'dpdDays') - getNum(a, 'dpdDays'));
    else if (sortBy === 'overdue') arr.sort((a, b) => getNum(b, 'totalOverdueAmount') - getNum(a, 'totalOverdueAmount'));
    else if (sortBy === 'outstanding') arr.sort((a, b) => {
      const ob = Number(b.loan?.outstandingPrincipal ?? 0) + Number(b.loan?.outstandingInterest ?? 0) + Number(b.loan?.penaltyAmount ?? 0);
      const oa = Number(a.loan?.outstandingPrincipal ?? 0) + Number(a.loan?.outstandingInterest ?? 0) + Number(a.loan?.penaltyAmount ?? 0);
      return ob - oa;
    });
    else if (sortBy === 'dpd') arr.sort((a, b) => getNum(b, 'dpdDays') - getNum(a, 'dpdDays'));
    else if (sortBy === 'recent') arr.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
    else if (sortBy === 'scheme') arr.sort((a, b) => (a.loan?.schemeName || '').localeCompare(b.loan?.schemeName || ''));
    return arr;
  }, [allCases, sortBy]);

  const schemeBreakdown = useMemo(() => {
    const m = new Map();
    allCases.forEach((c) => {
      const name = c.loan?.schemeName || c.loan?.productType || 'Unknown';
      const e = m.get(name) || { name, count: 0, overdue: 0, bounced: 0 };
      e.count += 1;
      e.overdue += Number(c.loan?.totalOverdueAmount || 0);
      e.bounced += Number(c.loan?.bouncedEmis || 0);
      m.set(name, e);
    });
    return Array.from(m.values()).sort((a, b) => b.overdue - a.overdue);
  }, [allCases]);

  const topCases = sortedCases.slice(0, 8);

  return (
    <OwnerLayout title="Loan Dashboard">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-navy text-lg">Loan Portfolio & Recovery</h2>
          <p className="text-sm text-gray">Track overdue accounts, broken promises, and field collections</p>
        </div>
        <div className="flex gap-2 items-center">
          {branches.length > 1 && (
            <select className="form-input" style={{ width: 'auto', minWidth: '160px' }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">All branches</option>
              {branches.map((b) => (<option key={b.id} value={b.id}>{b.branchName}</option>))}
            </select>
          )}
          <button className="btn-outline" onClick={load} title="Refresh">
            <RefreshCcw size={14} style={{ marginRight: '4px' }} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats grid - same pattern as Owner Dashboard */}
      <div className="od-stats">
        <div className="od-stat clickable" onClick={() => navigate('/owner/recovery/cases')}>
          <div className="od-stat-lbl">Active Cases</div>
          <div className="od-stat-val">{summary?.totalCases ?? 0}</div>
          <div className="od-stat-sub">Open recovery accounts</div>
        </div>
        <div className="od-stat accent clickable" onClick={() => navigate('/owner/recovery/loans')}>
          <div className="od-stat-lbl">Total Overdue</div>
          <div className="od-stat-val red">{fmtINR(summary?.totalOverdueAmount)}</div>
          <div className="od-stat-sub">Across all DPD buckets</div>
        </div>
        <div className="od-stat clickable" onClick={() => navigate('/owner/recovery/cases?status=ptp_set')}>
          <div className="od-stat-lbl">Broken Promises</div>
          <div className="od-stat-val">{summary?.brokenPtpCount ?? 0}</div>
          <div className="od-stat-sub">PTPs missed</div>
        </div>
        <div className="od-stat clickable" onClick={() => navigate('/owner/recovery/customers')}>
          <div className="od-stat-lbl">Customers</div>
          <div className="od-stat-val">{allCases.length > 0 ? new Set(allCases.map((c) => c.clientId)).size : 0}</div>
          <div className="od-stat-sub">Manage borrower records</div>
        </div>
      </div>

      {/* Money collected KPIs */}
      <div className="od-stats" style={{ marginTop: '12px' }}>
        <div className="od-stat clickable" style={{ borderLeft: '3px solid var(--green, #22c55e)' }} onClick={openTodayPayments}>
          <div className="od-stat-lbl"><HandCoins size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Collected Today</div>
          <div className="od-stat-val" style={{ color: 'var(--green, #16a34a)' }}>{fmtINR(summary?.collectedToday?.amount)}</div>
          <div className="od-stat-sub">{summary?.collectedToday?.count || 0} payment{(summary?.collectedToday?.count || 0) !== 1 ? 's' : ''} · click to view</div>
        </div>
        <div className="od-stat clickable" style={{ borderLeft: '3px solid var(--green, #22c55e)' }} onClick={openYesterdayPayments}>
          <div className="od-stat-lbl"><HandCoins size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Collected Yesterday</div>
          <div className="od-stat-val" style={{ color: 'var(--green, #16a34a)' }}>{fmtINR(summary?.collectedYesterday?.amount)}</div>
          <div className="od-stat-sub">{summary?.collectedYesterday?.count || 0} payment{(summary?.collectedYesterday?.count || 0) !== 1 ? 's' : ''} · click to view</div>
        </div>
        <div className="od-stat clickable" style={{ borderLeft: '3px solid var(--green, #22c55e)' }} onClick={openWeekPayments}>
          <div className="od-stat-lbl"><TrendingUp size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />This Week</div>
          <div className="od-stat-val" style={{ color: 'var(--green, #16a34a)' }}>{fmtINR(summary?.collectedThisWeek?.amount)}</div>
          <div className="od-stat-sub">{summary?.collectedThisWeek?.count || 0} payment{(summary?.collectedThisWeek?.count || 0) !== 1 ? 's' : ''} · since Mon</div>
        </div>
        <div className="od-stat clickable" style={{ borderLeft: '3px solid var(--green, #22c55e)' }} onClick={openMonthPayments}>
          <div className="od-stat-lbl"><TrendingUp size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />This Month</div>
          <div className="od-stat-val" style={{ color: 'var(--green, #16a34a)' }}>{fmtINR(summary?.collectedThisMonth?.amount)}</div>
          <div className="od-stat-sub">{summary?.collectedThisMonth?.count || 0} payment{(summary?.collectedThisMonth?.count || 0) !== 1 ? 's' : ''}</div>
        </div>
        <div className="od-stat clickable" onClick={openAllPayments}>
          <div className="od-stat-lbl"><Wallet size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />All Time</div>
          <div className="od-stat-val">{fmtINR(summary?.collectedAllTime?.amount)}</div>
          <div className="od-stat-sub">{summary?.collectedAllTime?.count || 0} total payment{(summary?.collectedAllTime?.count || 0) !== 1 ? 's' : ''}</div>
        </div>
      </div>

      {/* Loan accounts closed KPIs */}
      <div className="od-stats" style={{ marginTop: '12px' }}>
        <div className="od-stat" style={{ borderLeft: '3px solid #6366f1' }}>
          <div className="od-stat-lbl"><XCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Closed Today</div>
          <div className="od-stat-val" style={{ color: '#4f46e5' }}>{summary?.closedToday ?? 0}</div>
          <div className="od-stat-sub">loan account{(summary?.closedToday ?? 0) !== 1 ? 's' : ''} closed</div>
        </div>
        <div className="od-stat" style={{ borderLeft: '3px solid #6366f1' }}>
          <div className="od-stat-lbl"><XCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Closed Yesterday</div>
          <div className="od-stat-val" style={{ color: '#4f46e5' }}>{summary?.closedYesterday ?? 0}</div>
          <div className="od-stat-sub">loan account{(summary?.closedYesterday ?? 0) !== 1 ? 's' : ''} closed</div>
        </div>
        <div className="od-stat" style={{ borderLeft: '3px solid #6366f1' }}>
          <div className="od-stat-lbl"><XCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Closed This Week</div>
          <div className="od-stat-val" style={{ color: '#4f46e5' }}>{summary?.closedThisWeek ?? 0}</div>
          <div className="od-stat-sub">since Monday</div>
        </div>
        <div className="od-stat" style={{ borderLeft: '3px solid #6366f1' }}>
          <div className="od-stat-lbl"><XCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Closed This Month</div>
          <div className="od-stat-val" style={{ color: '#4f46e5' }}>{summary?.closedThisMonth ?? 0}</div>
          <div className="od-stat-sub">this month</div>
        </div>
      </div>

      {/* Waived off KPIs */}
      <div className="od-stats" style={{ marginTop: '12px' }}>
        <div className="od-stat" style={{ borderLeft: '3px solid #f59e0b' }}>
          <div className="od-stat-lbl"><Scissors size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Waived Today</div>
          <div className="od-stat-val" style={{ color: '#b45309' }}>{fmtINR(summary?.waivedToday?.amount)}</div>
          <div className="od-stat-sub">{summary?.waivedToday?.count || 0} waiver{(summary?.waivedToday?.count || 0) !== 1 ? 's' : ''}</div>
        </div>
        <div className="od-stat" style={{ borderLeft: '3px solid #f59e0b' }}>
          <div className="od-stat-lbl"><Scissors size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Waived Yesterday</div>
          <div className="od-stat-val" style={{ color: '#b45309' }}>{fmtINR(summary?.waivedYesterday?.amount)}</div>
          <div className="od-stat-sub">{summary?.waivedYesterday?.count || 0} waiver{(summary?.waivedYesterday?.count || 0) !== 1 ? 's' : ''}</div>
        </div>
        <div className="od-stat" style={{ borderLeft: '3px solid #f59e0b' }}>
          <div className="od-stat-lbl"><Scissors size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Waived This Week</div>
          <div className="od-stat-val" style={{ color: '#b45309' }}>{fmtINR(summary?.waivedThisWeek?.amount)}</div>
          <div className="od-stat-sub">{summary?.waivedThisWeek?.count || 0} waiver{(summary?.waivedThisWeek?.count || 0) !== 1 ? 's' : ''} · since Mon</div>
        </div>
        <div className="od-stat" style={{ borderLeft: '3px solid #f59e0b' }}>
          <div className="od-stat-lbl"><Scissors size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Waived This Month</div>
          <div className="od-stat-val" style={{ color: '#b45309' }}>{fmtINR(summary?.waivedThisMonth?.amount)}</div>
          <div className="od-stat-sub">{summary?.waivedThisMonth?.count || 0} waiver{(summary?.waivedThisMonth?.count || 0) !== 1 ? 's' : ''}</div>
        </div>
      </div>

      {/* Top collectors this month */}
      {(summary?.topCollectorsThisMonth?.length || 0) > 0 && (
        <div className="panel" style={{ marginTop: '16px' }}>
          <div className="panel-header">
            <span className="panel-title"><Trophy size={14} /> Top Collectors · This Month</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'var(--g1)', borderBottom: '1px solid var(--g2)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px', width: '40px' }}>#</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Agent</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Payments</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {summary.topCollectorsThisMonth.map((c, i) => (
                <tr
                  key={c.userId || i}
                  style={{ borderBottom: '1px solid var(--g2)', cursor: 'pointer' }}
                  onClick={() => openCollector(c)}
                >
                  <td style={{ padding: '8px 12px', color: 'var(--g3)' }}>{i + 1}</td>
                  <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--navy)' }}>{c.name}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{c.count}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <strong style={{ color: 'var(--green, #16a34a)' }}>{fmtINR(c.amount)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedCollector && (
        <div
          onClick={closeCollector}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(720px, 100%)', maxHeight: '90vh', overflow: 'hidden', background: '#fff', borderRadius: '16px', boxShadow: '0 24px 80px rgba(0,0,0,0.16)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--g2)' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '15px' }}>Collections by {selectedCollector.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--g3)' }}>{selectedCollector.count} payments · {fmtINR(selectedCollector.amount)}</div>
              </div>
              <button className="btn-outline" onClick={closeCollector} style={{ minWidth: 'auto' }}>Close</button>
            </div>
            <div style={{ maxHeight: 'calc(90vh - 86px)', overflowY: 'auto' }}>
              {collectorLoading ? (
                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>Loading payments…</div>
              ) : collectorPayments.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>No payments available for this collector.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: 'var(--g1)', borderBottom: '1px solid var(--g2)' }}>
                      <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Customer</th>
                      <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Loan</th>
                      <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {collectorPayments.map((p) => (
                      <tr key={p.id} style={{ borderBottom: '1px solid var(--g2)' }}>
                        <td style={{ padding: '10px 14px', color: 'var(--g3)' }}>{new Date(p.collectedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                        <td style={{ padding: '10px 14px', minWidth: '180px' }}>
                          <div style={{ fontWeight: 600 }}>{p.customerName || p.clientId || 'Unknown'}</div>
                          {p.customerPhone && <div style={{ fontSize: '11px', color: 'var(--g3)' }}>{p.customerPhone}</div>}
                        </td>
                        <td style={{ padding: '10px 14px', color: 'var(--g3)' }}>{p.loanAccountNumber}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--green, #16a34a)' }}>{fmtINR(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {paymentsDrill && (
        <div
          onClick={closePayments}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(960px, 100%)', maxHeight: '92vh', overflow: 'hidden', background: '#fff', borderRadius: '16px', boxShadow: '0 24px 80px rgba(0,0,0,0.16)', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--g2)' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '15px' }}>{paymentsDrill.title}</div>
                <div style={{ fontSize: '12px', color: 'var(--g3)' }}>
                  {paymentsDrill.from ? `${paymentsDrill.from}${paymentsDrill.to && paymentsDrill.to !== paymentsDrill.from ? ` → ${paymentsDrill.to}` : ''}` : 'All time'}
                  {paymentsDrillData?.summary && (
                    <> · {paymentsDrillData.summary.totalPayments} payments · {fmtINR(paymentsDrillData.summary.totalAmount)} · {paymentsDrillData.summary.uniqueCustomers} customers</>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {paymentsDrillData?.items?.length > 0 && (
                  <button
                    className="btn-outline"
                    style={{ minWidth: 'auto' }}
                    onClick={() => {
                      const p = new URLSearchParams();
                      if (paymentsDrill.from) p.set('from', paymentsDrill.from);
                      if (paymentsDrill.to) p.set('to', paymentsDrill.to);
                      if (branchId) p.set('branchId', branchId);
                      p.set('format', 'csv');
                      const base = (api.defaults?.baseURL || '').replace(/\/$/, '');
                      window.open(`${base}/recovery/payments?${p.toString()}`, '_blank');
                    }}
                  >
                    Export CSV
                  </button>
                )}
                <button className="btn-outline" onClick={closePayments} style={{ minWidth: 'auto' }}>Close</button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {paymentsDrillLoading ? (
                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>Loading payments…</div>
              ) : !paymentsDrillData?.items?.length ? (
                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>No payments collected in this range.</div>
              ) : (
                <>
                  {paymentsDrillData.summary?.byMode?.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '12px 16px', borderBottom: '1px solid var(--g2)', background: 'var(--g1)' }}>
                      {paymentsDrillData.summary.byMode.map((m) => (
                        <span key={m.mode} style={{ fontSize: '11px', padding: '4px 10px', background: '#fff', borderRadius: '999px', border: '1px solid var(--g2)' }}>
                          <strong>{(m.mode || '').toUpperCase()}</strong> · {m.count} · {fmtINR(m.amount)}
                        </span>
                      ))}
                    </div>
                  )}
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: 'var(--g1)', borderBottom: '1px solid var(--g2)', position: 'sticky', top: 0 }}>
                        <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Time</th>
                        <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Customer</th>
                        <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Loan</th>
                        <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Agent</th>
                        <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Mode</th>
                        <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentsDrillData.items.map((p) => (
                        <tr
                          key={p.id}
                          style={{ borderBottom: '1px solid var(--g2)', cursor: 'pointer' }}
                          onClick={() => { closePayments(); navigate(`/owner/recovery/cases/${encodeLan(p.loanAccountNumber)}`); }}
                        >
                          <td style={{ padding: '10px 14px', color: 'var(--g3)', whiteSpace: 'nowrap' }}>
                            <div>{new Date(p.collectedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>
                            <div style={{ fontSize: '10px' }}>{new Date(p.collectedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                          </td>
                          <td style={{ padding: '10px 14px', minWidth: '180px' }}>
                            <div style={{ fontWeight: 600 }}>{p.customerName || p.clientId || 'Unknown'}</div>
                            {p.customerPhone && <div style={{ fontSize: '11px', color: 'var(--g3)' }}>{p.customerPhone}</div>}
                          </td>
                          <td style={{ padding: '10px 14px', color: 'var(--g3)', fontSize: '12px' }}>
                            <div>{p.loanAccountNumber}</div>
                            {p.schemeName && <div style={{ fontSize: '10px' }}>{p.schemeName}</div>}
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: '12px' }}>{p.collectedByName || '—'}</td>
                          <td style={{ padding: '10px 14px', fontSize: '11px' }}>
                            <span style={{ padding: '2px 8px', background: 'var(--g1)', borderRadius: '4px' }}>{(p.paymentMode || '').toUpperCase()}</span>
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--green, #16a34a)' }}>{fmtINR(p.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PTPs Kept Today (collapsed by default) */}
      {(summary?.ptpsKeptToday?.length || 0) > 0 && (
        <details className="panel" style={{ marginTop: '16px', borderLeft: '3px solid #16a34a' }}>
          <summary className="panel-header" style={{ cursor: 'pointer', listStyle: 'none' }}>
            <span className="panel-title" style={{ color: '#16a34a' }}>
              <CheckCircle2 size={14} /> PTPs Kept Today ({summary.ptpsKeptToday.length})
              <span style={{ fontSize: '11px', color: 'var(--g3)', fontWeight: 400, marginLeft: '6px' }}>(click to expand)</span>
            </span>
          </summary>
          <div style={{ padding: '4px 0' }}>
            {summary.ptpsKeptToday.map((m) => (
              <div
                key={m.id}
                className="od-feed-item"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/owner/recovery/cases/${encodeLan(m.loanAccountNumber)}`)}
              >
                <span className="od-feed-dot upi" style={{ background: '#16a34a' }} />
                <div className="od-feed-info" style={{ flex: 1 }}>
                  <div className="od-feed-text">
                    <strong>{m.customerName || m.clientId || '—'}</strong>
                    <span style={{ color: 'var(--g3)', fontWeight: 400 }}> · {m.loanAccountNumber}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--g3)' }}>
                    {m.status === 'kept' ? (
                      <>Promised {fmtINR(m.promiseAmount)} · <strong style={{ color: '#16a34a' }}>Paid {fmtINR(m.paidAmount)} ✓</strong></>
                    ) : (
                      <>Promised {fmtINR(m.promiseAmount)} · <strong style={{ color: '#b45309' }}>Paid {fmtINR(m.paidAmount)} (partial)</strong></>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* PTPs Due Today */}
      {(summary?.ptpsDueToday?.length || 0) > 0 && (
        <div className="panel" style={{ marginTop: '16px', borderLeft: '3px solid #f59e0b' }}>
          <div className="panel-header">
            <span className="panel-title" style={{ color: '#b45309' }}>
              <Clock size={14} /> PTPs Due Today ({summary.ptpsDueToday.length})
            </span>
            <span className="panel-link" onClick={() => navigate('/owner/recovery/cases?status=ptp_set')}>View cases</span>
          </div>
          <div style={{ padding: '4px 0' }}>
            {summary.ptpsDueToday.map((m) => (
              <div
                key={m.id}
                className="od-feed-item"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/owner/recovery/cases/${encodeLan(m.loanAccountNumber)}`)}
              >
                <span className="od-feed-dot upi" />
                <div className="od-feed-info" style={{ flex: 1 }}>
                  <div className="od-feed-text">
                    <strong>{m.customerName || m.clientId || '—'}</strong>
                    <span style={{ color: 'var(--g3)', fontWeight: 400 }}> · {m.loanAccountNumber}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--g3)' }}>
                    Promised {fmtINR(m.promiseAmount)} today
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming PTPs */}
      {(summary?.ptpsUpcoming?.length || 0) > 0 && (
        <div className="panel" style={{ marginTop: '16px', borderLeft: '3px solid #0ea5e9' }}>
          <div className="panel-header">
            <span className="panel-title" style={{ color: '#0369a1' }}>
              <CalendarClock size={14} /> Upcoming PTPs · Next 3 days ({summary.ptpsUpcoming.length})
            </span>
            <span className="panel-link" onClick={() => navigate('/owner/recovery/cases?status=ptp_set')}>View cases</span>
          </div>
          <div style={{ padding: '4px 0' }}>
            {summary.ptpsUpcoming.map((m) => (
              <div
                key={m.id}
                className="od-feed-item"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/owner/recovery/cases/${encodeLan(m.loanAccountNumber)}`)}
              >
                <span className="od-feed-dot upi" />
                <div className="od-feed-info" style={{ flex: 1 }}>
                  <div className="od-feed-text">
                    <strong>{m.customerName || m.clientId || '—'}</strong>
                    <span style={{ color: 'var(--g3)', fontWeight: 400 }}> · {m.loanAccountNumber}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--g3)' }}>
                    {fmtINR(m.promiseAmount)} by {m.promiseDate}
                    {m.daysUntil > 0 && <span style={{ color: '#0369a1', fontWeight: 600 }}> · in {m.daysUntil}d</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Missed PTPs */}
      {(summary?.missedPromises?.length || 0) > 0 && (
        <div className="panel" style={{ marginTop: '16px', borderLeft: '3px solid var(--red)' }}>
          <div className="panel-header">
            <span className="panel-title" style={{ color: 'var(--red)' }}>
              <AlertTriangle size={14} /> Missed Promises ({summary.missedPromises.length})
            </span>
            <span className="panel-link" onClick={() => navigate('/owner/recovery/cases?status=ptp_set')}>View cases</span>
          </div>
          <div style={{ padding: '4px 0' }}>
            {summary.missedPromises.map((m) => (
              <div
                key={m.id}
                className="od-feed-item"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/owner/recovery/cases/${encodeLan(m.loanAccountNumber)}`)}
              >
                <span className="od-feed-dot upi" />
                <div className="od-feed-info" style={{ flex: 1 }}>
                  <div className="od-feed-text">
                    <strong>{m.customerName || m.clientId || '—'}</strong>
                    <span style={{ color: 'var(--g3)', fontWeight: 400 }}> · {m.loanAccountNumber}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--g3)' }}>
                    Promised {fmtINR(m.promiseAmount)} by {m.promiseDate}
                    {m.daysOverdue > 0 && <span style={{ color: 'var(--red)', fontWeight: 600 }}> · {m.daysOverdue}d overdue</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="od-two-col" style={{ marginTop: '16px' }}>
        <div className="panel">
          <div className="panel-header">
            <span className="panel-title"><ClipboardList size={14} /> Top Cases</span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <ArrowDownAZ size={12} color="var(--g3)" />
              <select
                className="form-input"
                style={{ width: 'auto', minWidth: '160px', fontSize: '12px', padding: '4px 8px' }}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="bouncedEmis">Most Bounced EMIs</option>
                <option value="overdue">Highest Overdue</option>
                <option value="outstanding">Highest Outstanding</option>
                <option value="dpd">Highest DPD</option>
                <option value="scheme">By Scheme (A→Z)</option>
                <option value="recent">Recently Updated</option>
              </select>
              <span className="panel-link" onClick={() => navigate('/owner/recovery/cases')}>View all</span>
            </div>
          </div>
          <div style={{ padding: '8px 0' }}>
            {loading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--g3)', fontSize: '13px' }}>Loading…</div>
            ) : topCases.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--g3)', fontSize: '13px' }}>No recovery cases yet</div>
            ) : topCases.map((c) => {
              const bounced = c.loan?.bouncedEmis ?? 0;
              return (
                <div
                  key={c.id || c.loanAccountNumber}
                  className="od-feed-item"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/owner/recovery/cases/${encodeLan(c.loanAccountNumber)}`)}
                >
                  <span className={`od-feed-dot ${(c.loan?.dpdDays ?? 0) > 30 ? 'upi' : ''}`} />
                  <div className="od-feed-info" style={{ flex: 1 }}>
                    <div className="od-feed-text">
                      <strong>{c.customerName || c.clientId}</strong>
                      <span style={{ color: 'var(--g3)', fontWeight: 400 }}> · {c.loanAccountNumber}</span>
                      {c.loan?.schemeName && (
                        <span style={{ color: 'var(--g3)', fontWeight: 400, fontSize: '11px' }}> · {c.loan.schemeName}</span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--g3)' }}>
                      {STATUS_LABELS[c.status] || c.status} · DPD {c.loan?.dpdDays ?? 0}d ({c.loan?.dpdBucket || '0'}) ·
                      {' '}<span style={{ color: bounced > 0 ? 'var(--red)' : 'var(--g3)', fontWeight: bounced > 0 ? 600 : 400 }}>{bounced} EMI bounced</span> ·
                      {' '}<strong style={{ color: 'var(--red)' }}>{fmtINR(c.loan?.totalOverdueAmount)}</strong> overdue
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="panel">
          <div className="panel-header">
            <span className="panel-title"><HandCoins size={14} /> Quick Actions</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '12px 4px' }}>
            <button className="btn-outline" onClick={() => navigate('/owner/recovery/customers')}>
              <UserSquare2 size={14} style={{ marginRight: '6px' }} /> New Customer
            </button>
            <button className="btn-outline" onClick={() => navigate('/owner/recovery/loans')}>
              <Banknote size={14} style={{ marginRight: '6px' }} /> New Loan
            </button>
            <button className="btn-outline" onClick={() => navigate('/owner/recovery/cases?status=new_overdue')}>
              <AlertTriangle size={14} style={{ marginRight: '6px' }} /> New Overdue
            </button>
            <button className="btn-outline" onClick={() => navigate('/owner/recovery/cases?status=ptp_set')}>
              <BadgeAlert size={14} style={{ marginRight: '6px' }} /> PTP Worklist
            </button>
          </div>
          <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--g3)', padding: '0 4px 4px' }}>
            Tip: assign cases to agents from the Loans page using <strong>Assign</strong>.
          </div>
        </div>
      </div>

      {/* Scheme breakdown */}
      {schemeBreakdown.length > 0 && (
        <div className="panel" style={{ marginTop: '16px' }}>
          <div className="panel-header">
            <span className="panel-title"><Banknote size={14} /> Recovery by Scheme</span>
            <span style={{ fontSize: '11px', color: 'var(--g3)' }}>Click a scheme to see its open loans</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'var(--g1)', borderBottom: '1px solid var(--g2)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Scheme</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Active Cases</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Total Bounced EMI</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: 'var(--g3)', fontSize: '11px' }}>Total Overdue</th>
              </tr>
            </thead>
            <tbody>
              {schemeBreakdown.map((s) => (
                <tr
                  key={s.name}
                  style={{ borderBottom: '1px solid var(--g2)', cursor: 'pointer' }}
                  onClick={() => navigate(`/owner/recovery/cases?scheme=${encodeURIComponent(s.name)}`)}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--g1)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = ''}
                  title={`View ${s.count} active case${s.count !== 1 ? 's' : ''} under ${s.name}`}
                >
                  <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--navy)' }}>{s.name}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{s.count}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{s.bounced}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <strong style={{ color: 'var(--red)' }}>{fmtINR(s.overdue)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </OwnerLayout>
  );
}
